//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useTheme } from 'styled-components';

import { CaptionModificationType } from '@apis/scheduler-generative-ai/schedulerGenerativeAiModel';
import ActionButton from '@components/atoms/action-button/ActionButton';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import {
  getSmartInputTrackingId,
  replaceAllText,
} from '@components/molecules/smart-input/smartInputUtils';
import { MediumTooltip } from '@components/molecules/tooltip/Tooltip';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { SocialNetwork, getSocialNetworkName } from '@models/SocialNetwork';
import { formatTrackingId } from '@social-response/utils/format-tracking-id';
import {
  SMART_INPUT_AI_CAPTION_MENU_DISABLED_TOOLTIP,
  SMART_INPUT_AI_CAPTION_MENU_GENERATE_DISABLED_TOOLTIP,
} from '@universal-scheduling/components/scheduler/schedulerConstants';
import { isNullOrEmpty } from '@utils/stringUtils';

export const RecommendedCaptionDropdown = () => {
  const theme = useTheme();
  const smartInputContext = useSmartInputContext();
  const { recommendedCaption } = smartInputContext;

  const handleRecommendedCaptionClick = (
    value: CaptionModificationType | undefined,
  ) => {
    if (!recommendedCaption?.getRecommendedCaption) {
      return;
    }

    void recommendedCaption
      .getRecommendedCaption(value)
      .then((response: string) => {
        if (isNullOrEmpty(response)) {
          return;
        }

        replaceAllText(response, smartInputContext);
      });
  };

  if (!recommendedCaption?.isEnabled) {
    return null;
  }

  const { hasMedia, shouldDisableModification } = recommendedCaption;

  const dropdownConfig = getRecommendedCaptionDropdownOptions(
    recommendedCaption.socialNetwork,
  );

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        asChild
        disabled={recommendedCaption.isRecommendedCaptionLoading}
      >
        <ActionButton
          disabled={recommendedCaption.isRecommendedCaptionLoading}
          iconColor={theme.colors.gray9}
          iconName={
            recommendedCaption.isRecommendedCaptionLoading
              ? 'spinner'
              : 'sparkles'
          }
          label='Recommended Caption'
          size='small'
          trackingId={getSmartInputTrackingId(
            smartInputContext.trackingIdPrefix,
            SmartInputTrackingId.RecommendedCaptionActionBarButton,
          )}
        />
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align='end'>
        <DropdownMenu.Group>
          {dropdownConfig.options.map((option) => {
            const isModificationOptionDisabled =
              option.isModificationOption && shouldDisableModification;
            const isGenerateOptionDisabled =
              !hasMedia && !option.isModificationOption;
            const isOptionDisabled =
              isModificationOptionDisabled || isGenerateOptionDisabled;

            return (
              <React.Fragment key={option.label}>
                {!option.subOptions && (
                  <>
                    <DropdownMenu.Item
                      key={option.label}
                      disabled={isOptionDisabled}
                      onClick={
                        !isOptionDisabled
                          ? () => handleRecommendedCaptionClick(option.value)
                          : undefined
                      }
                      trackingId={`${SmartInputTrackingId.RecommendedCaptionDropdownConfig}-${formatTrackingId(option.label)}`}
                    >
                      {!isOptionDisabled ? (
                        option.label
                      ) : (
                        <MediumTooltip
                          content={option.tooltip}
                          placement='right'
                          target={<>{option.label}</>}
                        />
                      )}
                    </DropdownMenu.Item>
                  </>
                )}
                {option.subOptions && option.subOptions.length > 0 && (
                  <DropdownMenu.Sub>
                    <DropdownMenu.SubTrigger
                      disabled={isModificationOptionDisabled}
                    >
                      {!isModificationOptionDisabled ? (
                        option.label
                      ) : (
                        <MediumTooltip
                          content={option.tooltip}
                          placement='right'
                          target={<>{option.label}</>}
                        />
                      )}

                      <DropdownMenu.NavIcon />
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubContent>
                      {option.subOptions.map((subOption) => {
                        const trackingId = `${SmartInputTrackingId.RecommendedCaptionDropdownConfig}-${formatTrackingId(subOption.label)}`;

                        return (
                          <DropdownMenu.Item
                            key={subOption.label}
                            onClick={() =>
                              handleRecommendedCaptionClick(subOption.value)
                            }
                            trackingId={trackingId}
                          >
                            {subOption.label}
                          </DropdownMenu.Item>
                        );
                      })}
                    </DropdownMenu.SubContent>
                  </DropdownMenu.Sub>
                )}
              </React.Fragment>
            );
          })}
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

const getRecommendedCaptionDropdownOptions = (networkName: SocialNetwork) => ({
  options: [
    {
      isModificationOption: false,
      label: 'Generate new caption',
      tooltip: SMART_INPUT_AI_CAPTION_MENU_GENERATE_DISABLED_TOOLTIP,
      value: undefined,
    },
    {
      isModificationOption: true,
      label: 'Fix spelling and grammar',
      tooltip: SMART_INPUT_AI_CAPTION_MENU_DISABLED_TOOLTIP,
      value: CaptionModificationType.FixSpellingAndGrammar,
    },
    {
      isModificationOption: true,
      label: 'Make shorter',
      tooltip: SMART_INPUT_AI_CAPTION_MENU_DISABLED_TOOLTIP,
      value: CaptionModificationType.MakeShorter,
    },
    {
      isModificationOption: true,
      label: 'Make longer',
      tooltip: SMART_INPUT_AI_CAPTION_MENU_DISABLED_TOOLTIP,
      value: CaptionModificationType.MakeLonger,
    },
    {
      isModificationOption: true,
      label: 'Simplify language',
      tooltip: SMART_INPUT_AI_CAPTION_MENU_DISABLED_TOOLTIP,
      value: CaptionModificationType.SimplifyLanguage,
    },
    {
      isModificationOption: true,
      label: 'Change tone to...',
      tooltip: SMART_INPUT_AI_CAPTION_MENU_DISABLED_TOOLTIP,
      subOptions: [
        {
          isModificationOption: true,
          label: 'Professional',
          value: CaptionModificationType.ChangeToneToProfessional,
        },
        {
          isModificationOption: true,
          label: 'Casual',
          value: CaptionModificationType.ChangeToneToCasual,
        },
        {
          isModificationOption: true,
          label: 'Straightforward',
          value: CaptionModificationType.ChangeToneToStraightforward,
        },
        {
          isModificationOption: true,
          label: 'Confident',
          value: CaptionModificationType.ChangeToneToConfident,
        },
        {
          isModificationOption: true,
          label: 'Friendly',
          value: CaptionModificationType.ChangeToneToFriendly,
        },
      ],
    },
    {
      isModificationOption: true,
      label: `Optimize for ${getSocialNetworkName(networkName)}`,
      tooltip: SMART_INPUT_AI_CAPTION_MENU_DISABLED_TOOLTIP,
      value: CaptionModificationType.OptimizeForPlatform,
    },
  ],
});
